import PropTypes from "prop-types";
import React, { useEffect, useState, useRef } from "react";
import { getCaseRevisions, compareCaseRevisions } from "requests/homeowner/caseDataHistory";
import { MSpinner, MSelect, MTable } from "components/Shared";

export const CASE_DATA_REVISIONS_PRE_RELEASE = "CASE_DATA_REVISIONS_PRE_RELEASE";
export const CASE_DATA_REVISIONS_REJECTION = "CASE_DATA_REVISIONS_REJECTION";

export default function CaseFormDataRevisions({ controlNumber, defaultFromFriendlyName, defaultToFriendlyName }) {
  const [revisions, setRevisions] = useState();
  const [revisionFromId, setRevisionFromId] = useState(); //"2020-02-19-13-34-46");
  const [revisionToId, setRevisionToId] = useState(); //"2020-02-19-11-20-56");
  const [revisionRows, setRevisionRows] = useState();
  const mounted = useRef(true);

  const colDefs = [
    { name: "Source", dataSource: "field", addDepthWhitespace: true, className: "revision-source", },
    { name: "Before", dataSource: "before", width: "300px", },
    { name: "After", dataSource: "after", width: "300px", },
  ];

  useEffect(() => {
    getCaseRevisions(controlNumber, handleRevisions, mounted);
  }, [controlNumber]);

  useEffect(() => {
    if (revisionFromId && revisionToId) {
      setRevisionRows(null);
      compareCaseRevisions(controlNumber, revisionFromId, revisionToId, mapRevisionRows, mounted);
    }
  }, [revisionFromId, revisionToId]);

  const handleRevisions = response => {
    setRevisions(response);
    if (defaultFromFriendlyName && defaultToFriendlyName) {
      const sortedRevisions = [...response].sort((a, b) => new Date(a.DataModifiedByDate) - new Date(b.DataModifiedByDate));
      const revisionFrom = sortedRevisions.find(r => r.friendlyName === defaultFromFriendlyName);
      const revisionTo = sortedRevisions.find(r => r.friendlyName === defaultToFriendlyName);
      if (revisionFrom) setRevisionFromId(revisionFrom.revisionId);
      if (revisionTo) setRevisionToId(revisionTo.revisionId);
    }
  };

  const mapRevisionRows = revisionData => {
    let data = [];

    const getLabel = o => `${(o.before || o.after).label || (o.before || o.after).sectionName}`;

    const push = (field, before, after, depth) => data.push({ field: field.replace(/<.*?>/g, ""), before, after, depth });

    const mapChildren = (node, depth) => {
      push(getLabel(node), node.before && node.before.questionAnswer, node.after && node.after.questionAnswer, depth);

      if (node.children && node.children.length) node.children.filter(c => c.changeType).map(c => mapChildren(c, depth + 1));
    };

    //we apparently dont want to see these forms in this view right now
    const filterOutForms = [
      "191708A2-FEF1-4C0E-B0B8-64207D803E78", //Appraiser Photo Form	
      "176A8EB4-CAE1-4BC0-AC89-8FA6653DF98A", //URAR 1004 Appraisal Comps - Warning Rules	
      "20367328-00E6-428F-8688-82356DF3AE00", //EXT 1004P Property Conditions V6	
      "E5C8F17A-A468-4F68-886C-B9F90EBCCD54", //Structure Supplement V6	
    ];

    revisionData.forms.filter(f => f.changeType && filterOutForms.indexOf(f.formMetadata.rowKey) === -1).forEach(f => {
      push(f.formMetadata.name, "", "", 0);

      f.questionAnswerSummary.filter(qa => qa.changeType).forEach(qa => {
        push(getLabel(qa), qa.before && qa.before.questionAnswer, qa.after && qa.after.questionAnswer, 1);
        qa.children.filter(c => c.changeType).forEach(c => mapChildren(c, 2));
      });
    });

    // I was keying each row with the hash sent back in the response data but some questions have the same exact hash so that didnt work :(
    // Using index in this case is completely ok because the data is computed once, so it wont cause any rerendering issues
    data.forEach((d, i) => d.key = i);

    setRevisionRows(data);
  };

  const createOption = fileRevision => {
    let label = fileRevision.revisionId;

    if (fileRevision.friendlyName)
      label += ` ${fileRevision.friendlyName}`;

    return { 
      label: label, 
      value: fileRevision.revisionId
    };
  };

  if (!revisions) return <MSpinner />;

  const revisionFromOptions = revisions.map(r => createOption(r));
  const revisionToOptions = revisions.map(r => createOption(r));
  
  return (
    <div className="case-data-history">
      <div className="revision-dropdowns">
        <div className="selection-label">
          Before:
        </div>
        <MSelect
          className="revision-selection"
          options={revisionFromOptions}
          isClearable={false}
          onChange={v => setRevisionFromId(v.value)}
          value={{ label: revisionFromOptions.find(o => o.value === revisionFromId)?.label || revisionFromId, value: revisionFromId }}
        />
        <div className="selection-label">
          After:
        </div>
        <MSelect
          className="revision-selection"
          options={revisionToOptions}
          isClearable={false}
          onChange={v => setRevisionToId(v.value)}
          value={{ label: revisionToOptions.find(o => o.value === revisionToId)?.label || revisionToId, value: revisionToId }}
        />
      </div>
      { revisionFromId && revisionToId && !revisionRows && <MSpinner /> }
      { revisionRows && <MTable
          columnDefinitions={colDefs}
          data={revisionRows}
          keyProperty="key"
          depthProperty="depth"
          emptyTableText={`There are no differences between ${revisionFromId} and ${revisionToId}`}
        /> }
      </div>);
}

CaseFormDataRevisions.propTypes = {
  controlNumber: PropTypes.number,
  defaultFromFriendlyName: PropTypes.string,  // Optional file revision friendly name to set as default from selection
  defaultToFriendlyName: PropTypes.string, // Optional file revision friendly name to set as default to selection
};
